import React, { useState, useEffect } from 'react';
import {
  Box, Flex, Grid, GridItem, Text, Heading, Button, Input, Select,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, IconButton,
  HStack, VStack, Avatar, Badge, useColorModeValue, useToast,
  InputGroup, InputLeftElement, Card, CardBody, Spinner,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure, Center,
  Divider, Icon
} from '@chakra-ui/react';
import {
  MdPeople, MdAdd, MdEdit, MdDelete, MdSearch,
  MdFilterList, MdKeyboardArrowLeft, MdKeyboardArrowRight
} from 'react-icons/md';

import UserModal from './UserModal';
import { userService } from '../../../../services/authService';
import './AdminUsers.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();
  
  const toast = useToast();
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  const usersPerPage = 10;

  // Role labels in Portuguese
  const roleLabels = {
    admin: 'Administrador',
    associado: 'Associado',
    comercial: 'Comercial',
    contabilidade: 'Contabilidade',
    diretoria_geral: 'Diretoria Geral',
    diretoria_juridica: 'Diretoria Jurídica',
    diretoria_comercial: 'Diretoria Comercial',
    gestor_comercial: 'Gestor Comercial',
    gestor_juridico: 'Gestor Jurídico',
    juridico: 'Jurídico'
  };

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userService.getAll();
      if (response.success) {
        setUsers(response.data || []);
        setFilteredUsers(response.data || []);
      } else {
        toast({
          title: 'Erro ao carregar usuários',
          description: response.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erro ao carregar usuários',
        description: 'Erro ao comunicar com o servidor',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(user => 
        statusFilter === 'active' ? user.isActive : !user.isActive
      );
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter, users]);

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Handle add new user
  const handleAddUser = () => {
    setSelectedUser(null);
    onModalOpen();
  };

  // Handle edit user
  const handleEditUser = (user) => {
    setSelectedUser(user);
    onModalOpen();
  };

  // Handle delete user
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      const response = await userService.update(userToDelete._id, { isActive: false });
      if (response.success) {
        toast({
          title: 'Usuário desativado',
          description: 'O usuário foi desativado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchUsers();
      } else {
        toast({
          title: 'Erro ao desativar usuário',
          description: response.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erro ao desativar usuário',
        description: 'Erro ao comunicar com o servidor',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onDeleteClose();
      setUserToDelete(null);
    }
  };

  // Handle user save (create/update)
  const handleUserSave = async (userData) => {
    try {
      let response;
      if (selectedUser) {
        // Update existing user
        response = await userService.update(selectedUser._id, userData);
      } else {
        // Create new user
        response = await userService.create(userData);
      }

      if (response.success) {
        toast({
          title: selectedUser ? 'Usuário atualizado' : 'Usuário criado',
          description: selectedUser 
            ? 'O usuário foi atualizado com sucesso'
            : 'O novo usuário foi criado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchUsers();
        onModalClose();
      } else {
        toast({
          title: 'Erro ao salvar usuário',
          description: response.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erro ao salvar usuário',
        description: 'Erro ao comunicar com o servidor',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Box p={{ base: 4, md: 8 }}>
          {/* Header */}
          <Flex justify="space-between" align="center" mb={8}>
            <Box>
              <Heading size="lg" color={textColor}>
                Gerenciar Usuários
              </Heading>
              <Text color="gray.500" mt={1}>
                Gerencie todos os usuários do sistema
              </Text>
            </Box>
            <Button
              leftIcon={<MdAdd />}
              colorScheme="purple"
              onClick={handleAddUser}
              size={{ base: 'sm', md: 'md' }}
            >
              Novo Usuário
            </Button>
          </Flex>

          {/* Filters */}
          <Card bg={cardBg} mb={6}>
            <CardBody>
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                gap={4}
              >
                <GridItem>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <MdSearch color="gray.300" />
                    </InputLeftElement>
                    <Input
                      placeholder="Buscar por nome ou email"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </GridItem>
                <GridItem>
                  <Select
                    placeholder="Filtrar por função"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    {Object.entries(roleLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </GridItem>
                <GridItem>
                  <Select
                    placeholder="Filtrar por status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </Select>
                </GridItem>
              </Grid>
            </CardBody>
          </Card>

          {/* Users Table */}
          <Card bg={cardBg}>
            <CardBody>
              {loading ? (
                <Center py={10}>
                  <Spinner size="xl" color="purple.500" />
                </Center>
              ) : filteredUsers.length === 0 ? (
                <Center py={10}>
                  <VStack>
                    <Icon as={MdPeople} fontSize="48px" color="gray.400" />
                    <Text color="gray.500">Nenhum usuário encontrado</Text>
                  </VStack>
                </Center>
              ) : (
                <>
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Usuário</Th>
                          <Th>Função</Th>
                          <Th>Telefone</Th>
                          <Th>Status</Th>
                          <Th>Encaminhamento</Th>
                          <Th>Ações</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {currentUsers.map((user) => (
                          <Tr key={user._id}>
                            <Td>
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  name={user.name}
                                  bg="purple.500"
                                />
                                <Box>
                                  <Text fontWeight="medium">{user.name}</Text>
                                  <Text fontSize="sm" color="gray.500">
                                    {user.email}
                                  </Text>
                                </Box>
                              </HStack>
                            </Td>
                            <Td>
                              <Badge colorScheme="purple">
                                {roleLabels[user.role] || user.role}
                              </Badge>
                            </Td>
                            <Td>{user.phone || '-'}</Td>
                            <Td>
                              <Badge colorScheme={user.isActive ? 'green' : 'red'}>
                                {user.isActive ? 'Ativo' : 'Inativo'}
                              </Badge>
                            </Td>
                            <Td>
                              <Badge colorScheme={user.isForwardingRecipient ? 'green' : 'gray'}>
                                {user.isForwardingRecipient ? 'Habilitado' : 'Desabilitado'}
                              </Badge>
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <IconButton
                                  icon={<MdEdit />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={() => handleEditUser(user)}
                                  aria-label="Editar usuário"
                                />
                                <IconButton
                                  icon={<MdDelete />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() => handleDeleteClick(user)}
                                  aria-label="Deletar usuário"
                                />
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <>
                      <Divider my={4} />
                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color="gray.500">
                          Mostrando {indexOfFirstUser + 1} a {Math.min(indexOfLastUser, filteredUsers.length)} de {filteredUsers.length} usuários
                        </Text>
                        <HStack spacing={2}>
                          <IconButton
                            icon={<MdKeyboardArrowLeft />}
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            isDisabled={currentPage === 1}
                            aria-label="Página anterior"
                          />
                          <Text fontSize="sm">
                            Página {currentPage} de {totalPages}
                          </Text>
                          <IconButton
                            icon={<MdKeyboardArrowRight />}
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            isDisabled={currentPage === totalPages}
                            aria-label="Próxima página"
                          />
                        </HStack>
                      </Flex>
                    </>
                  )}
                </>
              )}
            </CardBody>
          </Card>
      </Box>

      {/* User Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        user={selectedUser}
        onSave={handleUserSave}
        roleLabels={roleLabels}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Desativar Usuário
            </AlertDialogHeader>

            <AlertDialogBody>
              Tem certeza que deseja desativar o usuário <strong>{userToDelete?.name}</strong>?
              Esta ação pode ser revertida posteriormente.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Desativar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default AdminUsers;
